import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Instagram, Linkedin, Menu, X } from "lucide-react";
import { LOGO, email, instagram, navItems } from "../data";
import { EASE, MaskedLines, Reveal } from "./Motion";

export function Logo() {
  return <img className="brand-logo" src={LOGO} alt="Moon Media" data-testid="moon-media-logo" />;
}

export function CTA({ children = "Start a Campaign", to = "/contact", variant = "dark", ...rest }) {
  const id = typeof children === "string" ? children.toLowerCase().replaceAll(" ", "-") : "link";
  return (
    <Link to={to} className={`button button-${variant}`} data-testid={`cta-${id}`} {...rest}>
      <span>{children}</span>
      <ArrowUpRight size={15} />
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return (
    <>
      <header className="site-header" data-testid="site-header">
        <Link to="/" className="logo-link" data-testid="header-logo-link"><Logo /></Link>
        <nav className="main-nav" data-testid="main-navigation">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={location.pathname === item.to ? "active" : ""}
              data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="header-right">
          <CTA to="/contact" variant="dark" className="header-cta" data-testid="nav-start-campaign">Start a Campaign</CTA>
          <button className="menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-button">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            className="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <nav className="mobile-nav">
              {navItems.map((item, i) => (
                <span className="mask-line" key={item.to}>
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.06, ease: EASE }}
                  >
                    <Link to={item.to} className={location.pathname === item.to ? "active" : ""} data-testid={`mobile-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}>
                      {item.label}
                    </Link>
                  </motion.span>
                </span>
              ))}
            </nav>
            <motion.div className="mobile-menu-footer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <CTA to="/contact" variant="gold">Start a Campaign</CTA>
              <a href={instagram} target="_blank" rel="noreferrer" data-testid="mobile-menu-instagram"><Instagram size={16} /> Instagram</a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-top">
        <div className="footer-brand">
          <Logo />
          <p className="footer-copy">Influencer marketing and creator partnerships built around relevance, authenticity and measurable campaign objectives.</p>
        </div>
        <div className="footer-links">
          <span className="eyebrow eyebrow-gold">Explore</span>
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} data-testid={`footer-${item.label.toLowerCase().replaceAll(" ", "-")}`}>{item.label}</Link>
          ))}
        </div>
        <div className="footer-links">
          <span className="eyebrow eyebrow-gold">Connect</span>
          <a href={instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram"><Instagram size={15} /> Instagram</a>
          <a href={`mailto:${email}`} data-testid="footer-email">{email}</a>
          <span className="muted-link" data-testid="footer-linkedin"><Linkedin size={15} /> LinkedIn — coming soon</span>
        </div>
      </div>
      <div className="footer-wordmark" aria-hidden="true">Moon <em>Media</em></div>
      <div className="footer-bottom">
        <span>© 2026 Moon Media. All rights reserved.</span>
        <span>moonmediapartnerships.com</span>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

export function PageIntro({ eyebrow, lines, text }) {
  return (
    <section className="page-intro">
      <Reveal as="span" className="eyebrow" y={14}>{eyebrow}</Reveal>
      <MaskedLines as="h1" className="page-title" lines={lines} data-testid="page-title" />
      {text && <Reveal as="p" className="intro-text" delay={0.35}>{text}</Reveal>}
    </section>
  );
}

export function CtaPanel({ eyebrow = "Ready when you are", lines, children = "Start a Campaign", to = "/contact" }) {
  return (
    <section className="dark-panel" data-testid="cta-panel">
      <Reveal as="span" className="eyebrow eyebrow-gold" y={14}>{eyebrow}</Reveal>
      <MaskedLines as="h2" inView lines={lines} />
      <Reveal delay={0.3}><CTA to={to} variant="gold">{children}</CTA></Reveal>
    </section>
  );
}
